package gr.tech.site.styles

import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.hover
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.JustifyContent

// ─── State card (50-card grid) ───────────────────────────────────────────────

/**
 * Applied to each state card div.
 *
 * Hover: slides up 4px, flips background to DarkGreen, text transitions to white.
 * All properties ease in-out over 320 ms so the card breathes rather than snapping.
 *
 * Child elements targeted by CSS class names:
 *   .state-abbr   — large abbreviation text
 *   .state-name   — smaller full-name text
 *   .state-footer — "↓ view utilities" strip
 */
val StateCardStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .height(100.percent)
            .position(Position.Relative)   // anchor for the page-arrow indicator
            .backgroundColor(Colors.White)
            .border(1.px, LineStyle.Solid, Theme.MediumGreen.rgb)
            .borderRadius(10.px)
            .overflow(Overflow.Hidden)
            .cursor(Cursor.Pointer)
            .transition(Transition.all(320.ms, AnimationTimingFunction.EaseInOut))
    }

    // ── Card-body hover (fires only when the footer is NOT being hovered) ──
    // :has() lets us check whether a descendant is the hovered element,
    // so hovering just the footer no longer triggers the whole-card flip.
    cssRule(":hover:not(:has(.state-footer:hover))") {
        Modifier
            .backgroundColor(Theme.DarkGreen.rgb)
            .border(1.px, LineStyle.Solid, Theme.DarkGreen.rgb)
            .translateY((-4).px)
            .boxShadow(
                BoxShadow.of(
                    offsetX = 0.px,
                    offsetY = 10.px,
                    blurRadius = 24.px,
                    spreadRadius = 0.px,
                    color = rgba(2, 67, 66, 0.28)
                )
            )
    }
    cssRule(":hover:not(:has(.state-footer:hover)) .state-abbr") {
        Modifier.color(Colors.White)
    }
    cssRule(":hover:not(:has(.state-footer:hover)) .state-name") {
        Modifier.color(rgba(255, 255, 255, 0.72))
    }
    cssRule(":hover:not(:has(.state-footer:hover)) .state-page-arrow") {
        Modifier.color(rgba(255, 255, 255, 0.6))
    }
    // Footer stays white while the card body is hovered — keeps them visually separate
    cssRule(":hover:not(:has(.state-footer:hover)) .state-footer") {
        Modifier.backgroundColor(Colors.White)
    }

    // ── Footer-only hover — independent dark-green strip ──────────────────
    cssRule(" .state-footer:hover") {
        Modifier
            .backgroundColor(Theme.DarkGreen.rgb)
            .color(rgba(255, 255, 255, 0.82))
            .borderTop(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.14))
    }
}

/** Large abbreviation label inside each state card */
val StateAbbrStyle = CssStyle.base {
    Modifier
        .fontSize(1.9.cssRem)
        .fontWeight(FontWeight.Bold)
        .color(Theme.DarkGreen.rgb)
        .lineHeight(1)
        .margin(bottom = 4.px)
        .transition(Transition.of("color", 320.ms, AnimationTimingFunction.EaseInOut))
}

/** Smaller full-name label inside each state card */
val StateNameStyle = CssStyle.base {
    Modifier
        .fontSize(0.72.cssRem)
        .color(rgba(0, 0, 0, 0.6))
        .textOverflow(TextOverflow.Ellipsis)
        .overflow(Overflow.Hidden)
        .whiteSpace(WhiteSpace.NoWrap)
        .transition(Transition.of("color", 320.ms, AnimationTimingFunction.EaseInOut))
}

/** "↓ view utilities" footer strip */
val StateFooterStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .borderTop(1.px, LineStyle.Solid, rgba(0, 0, 0, 0.09))
        .padding(topBottom = 5.px)
        .textAlign(TextAlign.Center)
        .fontSize(0.68.cssRem)
        .color(rgba(0, 0, 0, 0.38))
        .transition(Transition.all(320.ms, AnimationTimingFunction.EaseInOut))
}

/**
 * Small → arrow in the top-right of cards that have a dedicated state page.
 * Signals the card click will navigate away rather than open a modal.
 */
val StatePageArrowStyle = CssStyle.base {
    Modifier
        .position(Position.Absolute)
        .top(7.px)
        .right(9.px)
        .fontSize(0.68.cssRem)
        .color(Theme.MediumGreen.rgb)
        .opacity(0.75)
        .transition(
            listOf(
                Transition.of("color", 320.ms, AnimationTimingFunction.EaseInOut),
                Transition.of("opacity", 320.ms, AnimationTimingFunction.EaseInOut)
            )
        )
}

// ─── Utility modal card ───────────────────────────────────────────────────────

/** Outer card shell rendered inside ModalOverlay's Column */
val UtilityModalCardStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .maxWidth(500.px)
        .backgroundColor(Colors.White)
        .borderRadius(16.px)
        .overflow(Overflow.Hidden)
        .boxShadow(
            BoxShadow.of(
                offsetX = 0.px,
                offsetY = 24.px,
                blurRadius = 64.px,
                spreadRadius = 0.px,
                color = rgba(0, 0, 0, 0.28)
            )
        )
}

/** Dark green header strip at the top of the utility modal */
val UtilityModalHeaderStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .backgroundColor(Theme.DarkGreen.rgb)
        .padding(topBottom = 22.px, leftRight = 24.px)
        .display(DisplayStyle.Flex)
        .justifyContent(JustifyContent.SpaceBetween)
        .alignItems(AlignItems.FlexStart)
}

/** The × close button in the modal header */
val UtilityModalCloseStyle = CssStyle {
    base {
        Modifier
            .color(rgba(255, 255, 255, 0.55))
            .cursor(Cursor.Pointer)
            .transition(Transition.of("color", 180.ms))
    }
    hover {
        Modifier.color(Colors.White)
    }
}

/** Scrollable list area between header and footer */
val UtilityModalBodyStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .maxHeight(320.px)
        .overflow(Overflow.Auto)
}

/** Footer strip of the utility modal */
val UtilityModalFooterStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .backgroundColor(rgba(0, 0, 0, 0.025))
        .borderTop(1.px, LineStyle.Solid, rgba(0, 0, 0, 0.07))
        .padding(topBottom = 13.px, leftRight = 24.px)
        .display(DisplayStyle.Flex)
        .justifyContent(JustifyContent.SpaceBetween)
        .alignItems(AlignItems.Center)
}

/** "View full state page →" link in the modal footer */
val ModalFooterStatePageStyle = CssStyle {
    base {
        Modifier
            .fontSize(0.8.cssRem)
            .fontWeight(FontWeight.Bold)
            .color(Theme.AccentGreen.rgb)
            .textDecorationLine(TextDecorationLine.None)
            .transition(Transition.of("color", 180.ms))
    }
    hover {
        Modifier.color(Theme.DarkGreen.rgb)
    }
}

/** "Can't find yours? Contact us" link in the modal footer */
val ModalFooterContactStyle = CssStyle {
    base {
        Modifier
            .fontSize(0.78.cssRem)
            .color(Theme.DarkGreen.rgb)
            .opacity(0.65)
            .textDecorationLine(TextDecorationLine.None)
            .transition(Transition.of("color", 180.ms))
    }
    hover {
        Modifier.color(rgba(0, 0, 0, 0.65))
    }
}

// ─── Utility rows ─────────────────────────────────────────────────────────────

/**
 * Each utility row is a full-width link.
 * Hover: subtle green tint; arrow (class "util-arrow") nudges right.
 */
val UtilityRowStyle = CssStyle {
    base {
        Modifier
            .display(DisplayStyle.Flex)
            .justifyContent(JustifyContent.SpaceBetween)
            .alignItems(AlignItems.Center)
            .fillMaxWidth()
            .padding(topBottom = 13.px, leftRight = 24.px)
            .color(Theme.DarkGreen.rgb)
            .textDecorationLine(TextDecorationLine.None)
            .borderBottom(1.px, LineStyle.Solid, rgba(0, 0, 0, 0.06))
            .transition(Transition.of("background-color", 180.ms))
    }
    hover {
        Modifier.backgroundColor(rgba(2, 67, 66, 0.05))
    }
    cssRule(":hover .util-arrow") {
        Modifier
            .transition(Transition.all( 180.ms))
            .translateX(4.px)
    }
}

/** The → arrow icon inside each utility row */
val UtilityRowArrowStyle = CssStyle.base {
    Modifier
        .color(Theme.MediumGreen.rgb)
        .opacity(0.8)
        .transition(Transition.of("transform", 180.ms))
}
